if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Track which videos have been unmuted (for restart-on-unmute feature)
        this.unmutedVideos = new WeakSet();
        // Track which videos the user has manually paused
        this.userPausedVideos = new WeakSet();

        // Setup video autoplay behavior for PDP
        this.setupVideoAutoplay();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Helper: Setup unmute restart listener on a video
      setupUnmuteRestart(video) {
        if (!video || video.nodeName !== 'VIDEO') return;

        const handleVolumeChange = () => {
          // Only restart on first unmute
          if (!video.muted && !this.unmutedVideos.has(video)) {
            this.unmutedVideos.add(video);
            video.currentTime = 0;
            video.play();
          }
        };

        video.addEventListener('volumechange', handleVolumeChange);
      }

      // Helper: Load video muted and setup restart-on-unmute
      loadVideoMuted(deferredMedia) {
        if (!deferredMedia) return;

        const alreadyLoaded = deferredMedia.getAttribute('loaded');

        // Load content if not already loaded
        if (!alreadyLoaded) {
          deferredMedia.loadContent(false);
        }

        // Wait for video to be added to DOM, then mute it and setup restart
        setTimeout(() => {
          const video = deferredMedia.querySelector('video');
          if (video) {
            // Check if this video was already unmuted by user - preserve their settings
            const wasUnmuted = this.unmutedVideos.has(video);

            if (!wasUnmuted) {
              // First time loading or user hasn't unmuted yet - mute and hide controls
              video.muted = true;
              // Remove native controls on mobile - we use custom sound toggle
              if (!this.mql.matches) {
                video.removeAttribute('controls');
                video.controls = false;
              }
            }

            // Only auto-play if user hasn't manually paused this video
            if (!this.userPausedVideos.has(video)) {
              video.play().catch(() => {}); // Ignore autoplay errors
            }

            // Only setup listeners once
            if (!video.dataset.unmuteSetup) {
              video.dataset.unmuteSetup = 'true';
              this.setupUnmuteRestart(video);

              // Track when user manually pauses (only for unmuted videos with controls)
              video.addEventListener('pause', () => {
                // Only track as user-paused if video was unmuted (has controls)
                if (this.unmutedVideos.has(video)) {
                  this.userPausedVideos.add(video);
                }
              });

              // Clear paused state when user plays again
              video.addEventListener('play', () => {
                this.userPausedVideos.delete(video);
              });
            }
          }
        }, alreadyLoaded ? 0 : 50);
      }

      setupVideoAutoplay() {
        // Auto-load first video on page load (both desktop and mobile)
        this.autoplayFirstVideo();

        // Setup slide change handler to autoplay videos when switching slides
        this.setupSlideChangeAutoplay();

        // Setup click handlers for mobile video containers
        if (!this.mql.matches) {
          this.setupMobileClickHandlers();
        }

        // Setup sound toggle buttons
        this.setupSoundToggleButtons();
      }

      setupSoundToggleButtons() {
        // Use event delegation for sound toggle buttons
        this.addEventListener('click', (e) => {
          const soundToggle = e.target.closest('.video-sound-toggle');
          if (!soundToggle) return;

          e.preventDefault();
          e.stopPropagation();

          // Button is now a sibling of deferred-media, find via parent container
          const container = soundToggle.closest('.product-media-container');
          if (!container) return;

          const deferredMedia = container.querySelector('.deferred-media');
          if (!deferredMedia) return;

          const video = deferredMedia.querySelector('video');
          if (!video) return;

          // Toggle mute state
          const isMuted = video.muted;
          video.muted = !isMuted;

          // Update button state
          soundToggle.setAttribute('data-muted', String(!isMuted));

          // If unmuting for the first time, restart video and show controls on mobile
          if (isMuted && !this.unmutedVideos.has(video)) {
            this.unmutedVideos.add(video);
            video.currentTime = 0;
            video.play();
            // Show native controls on mobile after unmuting
            if (!this.mql.matches) {
              video.controls = true;
            }
          }
        });
      }

      autoplayFirstVideo() {
        // Find the first slide that contains a video (deferred-media)
        const allSlides = this.elements.viewer?.querySelectorAll('[data-media-id]');
        if (!allSlides) return;

        for (const slide of allSlides) {
          const deferredMedia = slide.querySelector('.deferred-media');
          if (deferredMedia) {
            // Small delay to ensure page is ready
            setTimeout(() => {
              this.loadVideoMuted(deferredMedia);
            }, 100);
            break; // Only autoplay the first video found
          }
        }
      }

      setupSlideChangeAutoplay() {
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce((event) => {
          const activeSlide = event.detail.currentElement;
          if (!activeSlide) return;

          // Pause videos in non-active slides only if they haven't been unmuted by user
          const allSlides = this.elements.viewer.querySelectorAll('[data-media-id]');
          allSlides.forEach((slide) => {
            if (slide !== activeSlide) {
              const video = slide.querySelector('video');
              // Only pause if user hasn't unmuted this video
              if (video && !this.unmutedVideos.has(video)) {
                video.pause();
              }
            }
          });

          // If active slide has a video, ensure it's playing
          const deferredMedia = activeSlide.querySelector('.deferred-media');
          if (deferredMedia) {
            this.loadVideoMuted(deferredMedia);
          }
        }, 100));
      }

      setupMobileClickHandlers() {
        const videoContainers = this.querySelectorAll('.product__modal-opener--video-mobile');
        videoContainers.forEach((container) => {
          container.addEventListener('click', (e) => {
            // Don't interfere if clicking the actual deferred-media button
            if (e.target.closest('.deferred-media__poster')) return;

            const deferredMedia = container.parentElement?.querySelector('.deferred-media');
            if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
              this.loadVideoMuted(deferredMedia);
              e.preventDefault();
              e.stopPropagation();
            }
          });
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Check if using vertical thumbnail layout
          const isVerticalLayout = this.dataset.desktopLayout === 'thumbnail_vertical';

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          // For vertical layout, scroll to the image smoothly
          if (isVerticalLayout && this.mql.matches) {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            const top = activeMediaRect.top + window.scrollY - 100;
            window.scrollTo({ top: top, behavior: 'smooth' });
            return;
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
